import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Controller,
  ControllerWrapper,
  FieldTypes,
  FieldWrapper,
  FlexRadioGroup,
  FormFieldsContainer,
  Inscription,
  Label,
  SelectDropdown,
  ShimmerBox,
  useFormContext,
  useWatch,
} from '@common-fe/common-fe';

import { Option } from '@/common/types';
import { EnrollmentFormField } from '@/modules/employee/Employee/EnrollmentModal/hooks/useEnrollmentForm';

import { CurrentPlanInterface } from './hooks/useGetEnrollmentSetupData';
import HealthPlanFields from './PlanFields/HealthPlanFields';
import PlanFields from './PlanFields/PlanFields';
import useGetElectionAmountQuery from './queries/useGetElectionAmount.query';
import EnrollmentConflictBanner from './EnrollmentConflictBanner';
import { PlanTypes, PlanTypesOptions } from './enrollmentModal.types';

interface Props {
  plansOptions?: Option[];
  isEdit?: boolean;
  planType?: PlanTypes;
  healthPlansOptions?: Option[];
  healthPlanLoading: boolean;
  planLoading: boolean;
  currentPlan?: CurrentPlanInterface;
  healthPlanCoverageOptions?: Option[];
  employeeAge?: number;
  healthPlanCoverageLevel?: string;
  conflictMessage?: string;
  onElectionAmountChange?: (min?: number, max?: number) => void;
  onPlanChange?: (planId: string) => void;
  onHealthPlanChange?: (healthPlanId: string) => void;
  onConflictClear?: () => void;
}

const EnrollmentSection: React.FC<Props> = ({
  plansOptions = [],
  healthPlansOptions = [],
  currentPlan,
  isEdit,
  planType,
  planLoading,
  healthPlanLoading,
  healthPlanCoverageOptions,
  employeeAge,
  healthPlanCoverageLevel,
  conflictMessage,
  onElectionAmountChange,
  onPlanChange,
  onHealthPlanChange,
  onConflictClear,
}) => {
  const { control, clearErrors } = useFormContext();
  const planFormValue = useWatch({ control, name: EnrollmentFormField.plan });
  const enrollmentDateFormValue = useWatch({ control, name: EnrollmentFormField.enrollmentDate });
  const healthPlanFormValue = useWatch({ control, name: EnrollmentFormField.healthPlan });

  const currentPlanType = useWatch({ control, name: EnrollmentFormField.type });

  const isConflictVisible = !!conflictMessage;

  const handleChangeDateChange = useCallback(() => {
    if (!isConflictVisible) return;
    onConflictClear?.();
    clearErrors([EnrollmentFormField.enrollmentDate, EnrollmentFormField.healthPlanEnrollmentDate]);
  }, [isConflictVisible, onConflictClear, clearErrors]);

  const { maxElectionAmount, minElectionAmount } = useGetElectionAmountQuery(
    planFormValue,
    enrollmentDateFormValue,
    currentPlan?.planStartDate
  );
  const isLoading = useMemo(() => planLoading || healthPlanLoading, [planLoading, healthPlanLoading]);

  const isPlanFieldsAvailable = useMemo(
    () =>
      (isEdit && planType === PlanTypes.SAVING) ||
      (!isLoading && !!planFormValue && currentPlanType === PlanTypes.SAVING),
    [isEdit, planType, isLoading, planFormValue, currentPlanType]
  );

  const isHealthPlanFieldsAvailable = useMemo(
    () =>
      (isEdit && planType === PlanTypes.HEALTH) ||
      (!isLoading && !!healthPlanFormValue && currentPlanType === PlanTypes.HEALTH),
    [isEdit, planType, isLoading, healthPlanFormValue, currentPlanType]
  );

  useEffect(() => {
    if (onElectionAmountChange) {
      onElectionAmountChange(minElectionAmount, maxElectionAmount);
    }
  }, [minElectionAmount, maxElectionAmount, onElectionAmountChange]);

  return (
    <FormFieldsContainer gap="m" data-testid="enrollment-form">
      <Box
        background="canvas"
        round="container1Round"
        pad="spacing24"
        border={{ size: '1px', color: 'border2' }}
        gap="m"
      >
        <Inscription size="20px">Enrollment</Inscription>
        {((!!healthPlansOptions.length && !!plansOptions.length) || (!!planType && !!isEdit)) && (
          <FieldWrapper align="flex-start" style={{ justifyContent: 'space-between' }}>
            <Label showRequireIcon name={EnrollmentFormField.type} isNarrow>
              Type
            </Label>
            <ControllerWrapper style={{ flex: 'none' }}>
              <Controller
                name={EnrollmentFormField.type}
                control={control}
                render={({ field }) => (
                  <FlexRadioGroup
                    type={FieldTypes.Radio}
                    disabled={isEdit}
                    name={EnrollmentFormField.type}
                    value={field.value}
                    onChange={field.onChange}
                    options={PlanTypesOptions}
                    inputStyle={{ minWidth: '320px', maxWidth: '320px' }}
                  />
                )}
              />
            </ControllerWrapper>
          </FieldWrapper>
        )}
        <FieldWrapper style={{ justifyContent: 'space-between' }}>
          {(planType === PlanTypes.SAVING && plansOptions?.length) || currentPlanType === PlanTypes.SAVING ? (
            <>
              <Label showRequireIcon name={EnrollmentFormField.plan} isNarrow>
                Plan
              </Label>
              <ControllerWrapper style={{ flex: 'none' }}>
                <Controller
                  name={EnrollmentFormField.plan}
                  control={control}
                  render={({ field, fieldState }) => (
                    <SelectDropdown
                      id="plan"
                      data-testid="plan"
                      name={EnrollmentFormField.plan}
                      options={plansOptions}
                      singleMode
                      onChange={(value) => {
                        field.onChange(value);
                        if (onPlanChange && typeof value === 'string') onPlanChange(value);
                      }}
                      values={field?.value ? [field.value] : ['']}
                      placeholder="Select saving plan"
                      disabled={isEdit}
                      errorText={fieldState.error?.message}
                      inputStyle={{ minWidth: '320px', maxWidth: '320px' }}
                    />
                  )}
                />
              </ControllerWrapper>
            </>
          ) : (
            <>
              <Label showRequireIcon name={EnrollmentFormField.healthPlan} isNarrow>
                Health plan
              </Label>
              <ControllerWrapper style={{ flex: 'none' }}>
                <Controller
                  name={EnrollmentFormField.healthPlan}
                  control={control}
                  render={({ field, fieldState }) => (
                    <SelectDropdown
                      id="healthPlan"
                      data-testid="healthPlan"
                      name={EnrollmentFormField.healthPlan}
                      options={healthPlansOptions}
                      singleMode
                      errorText={fieldState.error?.message}
                      values={field?.value ? [field.value] : ['']}
                      onChange={(value) => {
                        field.onChange(value);
                        if (onHealthPlanChange && typeof value === 'string') onHealthPlanChange(value);
                      }}
                      placeholder="Select health plan"
                      disabled={isEdit}
                      inputStyle={{ minWidth: '320px', maxWidth: '320px' }}
                    />
                  )}
                />
              </ControllerWrapper>
            </>
          )}
        </FieldWrapper>
        {isLoading ? (
          <Box width="100%">
            <ShimmerBox width="100%" height="520px" />
          </Box>
        ) : (
          <>
            {isPlanFieldsAvailable && (
              <PlanFields
                isEdit={isEdit}
                maxElectionAmount={maxElectionAmount}
                minElectionAmount={minElectionAmount}
                employeeAge={employeeAge}
                currentPlan={currentPlan}
                onChangeDateChange={handleChangeDateChange}
              />
            )}
            {isHealthPlanFieldsAvailable && (
              <HealthPlanFields
                isEdit={isEdit}
                healthPlanCoverageOptions={healthPlanCoverageOptions}
                defaultHealthPlanCoverageLevel={healthPlanCoverageLevel}
                onChangeDateChange={handleChangeDateChange}
              />
            )}
          </>
        )}
        {isConflictVisible && <EnrollmentConflictBanner message={conflictMessage} />}
      </Box>
    </FormFieldsContainer>
  );
};

export default EnrollmentSection;
